import { riskColor, tok } from './risk'

export type StripBarrier = { code: string; confidence: number; threshold: number; stage: 'pre' | 'post' }

/**
 * The signature underwriting viz: all barriers as a threshold-ticked strip, split
 * prevention | mitigation. Bar height + colour = confidence; the tick = threshold,
 * so a bar stopping below its tick reads instantly as "fails the line".
 */
export function BarrierStrip({ barriers, height = 220 }: { barriers: StripBarrier[]; height?: number }) {
  const W = 560
  const H = height
  if (barriers.length === 0) {
    return (
      <div className="grid place-items-center text-[12px] text-faint" style={{ height: H }}>
        No barrier profile yet — answer the assessment to build it.
      </div>
    )
  }
  const pre = barriers.filter((b) => b.stage === 'pre')
  const post = barriers.filter((b) => b.stage === 'post')
  const ordered = [...pre, ...post]
  const pad = 8
  const gapMid = 30
  const n = ordered.length || 1
  const slot = (W - pad * 2 - gapMid) / n
  const bw = Math.min(17, slot * 0.52)
  const top = 30
  const base = H - 32
  const span = base - top
  const yF = (v: number) => base - (v / 100) * span
  const divX = pad + pre.length * slot + gapMid / 2

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 'auto' }}>
        {pre.length > 0 && (
          <text
            x={pad + (pre.length * slot) / 2}
            y={14}
            textAnchor="middle"
            fontSize={10}
            fontWeight={600}
            letterSpacing="0.1em"
            fontFamily="var(--font-display)"
            fill={tok('--faint')}
          >
            PREVENTION
          </text>
        )}
        {post.length > 0 && (
          <text
            x={divX + gapMid / 2 + (post.length * slot) / 2}
            y={14}
            textAnchor="middle"
            fontSize={10}
            fontWeight={600}
            letterSpacing="0.1em"
            fontFamily="var(--font-display)"
            fill={tok('--faint')}
          >
            MITIGATION &amp; RECOVERY
          </text>
        )}
        <line x1={divX} y1={20} x2={divX} y2={base + 6} stroke={tok('--border')} strokeDasharray="2 4" />
        {ordered.map((b, i) => {
          const x = pad + slot * i + (i >= pre.length ? gapMid : 0) + (slot - bw) / 2
          const h = (b.confidence / 100) * span
          const y = base - h
          const under = b.confidence < b.threshold
          return (
            <g key={b.code}>
              <rect x={x} y={top} width={bw} height={span} rx={bw / 2} fill={tok('--surface-2')} />
              <rect x={x} y={y} width={bw} height={h} rx={bw / 2} fill={riskColor(b.confidence)} />
              <line
                x1={x - 3}
                y1={yF(b.threshold)}
                x2={x + bw + 3}
                y2={yF(b.threshold)}
                stroke={tok('--ink')}
                strokeWidth={1.5}
              />
              <text
                x={x + bw / 2}
                y={base + 15}
                textAnchor="middle"
                fontSize={9}
                fontFamily="var(--font-display)"
                fill={under ? tok('--flare') : tok('--faint')}
              >
                {b.code}
              </text>
              {under && (
                <text x={x + bw / 2} y={base + 24} textAnchor="middle" fontSize={7} fill={tok('--flare')}>
                  ▼
                </text>
              )}
            </g>
          )
        })}
      </svg>
      <div className="mt-2 text-center text-[11px] text-faint">
        height + colour = confidence · tick = threshold · ▼ below the line · click any bar → evidence
      </div>
    </div>
  )
}
